// The scoped Documents block shared by delivery detail drawers: one entity's
// slice of the program documents (a company assignment's, or a person's),
// with a click-to-preview file name and an in-place upload. The rows are
// program documents (pangaea system of record); the upload is the same
// artifact-then-record two-step every other delivery doc surface uses.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraSectionHeader } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { FileUploader } from '../../../../components/ProjectList/FileUploader';
import { useAuth } from '../../../../contexts/authContext';
import { useArtifactNames, useUploadArtifacts } from '../../../../hooks/useDeliveryDocuments';
import { useCreateProgramDocument, useScopedProgramDocuments } from '../../Docs/hooks';
import { DEFAULT_COMPANY_DOCUMENT_CATEGORY, DOCUMENT_CATEGORY_OPTIONS } from '../constants/documentCategories';
import { DocumentPreviewDialog } from './DocumentPreviewDialog';
// No option in the shared list describes a résumé (contact scope) or an
// issue attachment (issue scope), so one default can't serve all three
// scopes — derive it per scope instead of hardcoding a single value. Shared
// by the lazy initializer and the scope-change reset below so they can't drift.
function defaultCategoryFor(scope, defaultCategory) {
    return defaultCategory ?? (scope.companyAssignmentId ? DEFAULT_COMPANY_DOCUMENT_CATEGORY : 'Other');
}
export function DrawerDocumentsSection({ programId, scope, emptyText = 'No documents yet.', testid, defaultCategory, canEdit = true }) {
    const { user } = useAuth();
    const docsQuery = useScopedProgramDocuments(programId, scope);
    const docs = docsQuery.data ?? [];
    const upload = useUploadArtifacts();
    const createDoc = useCreateProgramDocument();
    const [stagedFiles, setStagedFiles] = useState([]);
    const [previewing, setPreviewing] = useState(null);
    const [category, setCategory] = useState(()=>defaultCategoryFor(scope, defaultCategory));
    // Entity-to-entity switching without unmounting isn't a live path today:
    // SpecraOverlayDrawer (packages/ui-components/src/components/specra/captains/
    // overlay-drawer.tsx) defaults modal=true, and none of this section's mount
    // sites (ProgramRolesTab, ProgramCompaniesTab, IssuesTab) pass modal={false},
    // so the Radix scrim blocks clicks on the rows underneath an open drawer —
    // confirmed in browser validation, where clicking a second row timed out with
    // "subtree intercepts pointer events" on both tabs. This reset is a guard
    // against that changing, not proof it happens now: a caller that renders this
    // section non-modally, or a drawer that later drops `modal`, would otherwise
    // carry the previous entity's chosen category and staged files into the next
    // entity's upload. React's documented "adjust state during render" pattern
    // resets synchronously, before paint, so the stale category never flashes the
    // way it would from a useEffect.
    const scopeKey = scope.companyAssignmentId ?? scope.contactId ?? scope.issueId;
    const [prevScopeKey, setPrevScopeKey] = useState(scopeKey);
    if (scopeKey !== prevScopeKey) {
        setPrevScopeKey(scopeKey);
        setCategory(defaultCategoryFor(scope, defaultCategory));
        setStagedFiles([]);
        setPreviewing(null);
    }
    // Row file names resolve through the artifact plane, fail-closed: an id
    // that doesn't resolve renders as a non-clickable stub (same posture as
    // the task attachments drawer).
    const fileNameById = useArtifactNames(useMemo(()=>docs.map((d)=>d.fileId), [
        docs
    ]));
    const busy = upload.isPending || createDoc.isPending;
    const categoryFieldId = useId();
    const handleUpload = async ()=>{
        if (stagedFiles.length === 0) return;
        try {
            const res = await upload.mutateAsync({
                files: stagedFiles
            });
            const succeededFiles = new Set(res.files.map((f)=>f.file));
            // Failed uploads stay staged so the user can retry just those;
            // successful ones are recorded as ProgramDocuments below, one per file.
            // Matched by File identity, not name — two staged files can share a
            // name, and a name-based filter would drop both on a partial failure.
            setStagedFiles(stagedFiles.filter((f)=>!succeededFiles.has(f)));
            const errors = res.failed.map((f)=>`${f.name}: ${f.error}`);
            for (const f of res.files){
                try {
                    await createDoc.mutateAsync({
                        programId,
                        companyAssignmentId: scope.companyAssignmentId,
                        contactId: scope.contactId,
                        issueId: scope.issueId,
                        title: f.name || 'Document',
                        description: '',
                        // No doc-date field on this inline flow; stamp the upload date so
                        // the row sorts sensibly rather than landing at the epoch.
                        docDate: new Date(),
                        fileId: f.artifactId,
                        // Server resolves the display filename from the artifact and
                        // derives the uploader from the caller — both are ignored here.
                        fileName: '',
                        category,
                        uploadedBy: user?.email ?? ''
                    });
                } catch (err) {
                    errors.push(`${f.name}: ${err instanceof Error ? err.message : 'Failed to record document'}`);
                }
            }
            if (errors.length > 0) {
                toast.error(errors.join('; '));
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to upload document');
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": testid ?? Testids.deliveryShared.drawerDocs,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Documents"
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-2",
                children: [
                    docsQuery.isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                        children: "Loading…"
                    }),
                    !docsQuery.isLoading && docs.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                        children: emptyText
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-1.5",
                        children: docs.map((doc)=>{
                            const name = fileNameById.get(doc.fileId);
                            return /*#__PURE__*/ _jsxs("li", {
                                className: "flex items-baseline justify-between gap-2 text-[length:var(--fs-ui-body)]",
                                "data-testid": Testids.deliveryShared.drawerDocRow,
                                "data-row-id": doc.id,
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "min-w-0 truncate",
                                        children: doc.fileId ? /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            className: "max-w-full cursor-pointer truncate text-primary hover:underline",
                                            title: `Open ${doc.title || name || 'document'}`,
                                            "data-testid": Testids.deliveryShared.drawerDocOpen,
                                            onClick: ()=>setPreviewing(doc),
                                            children: doc.title || name || 'Document'
                                        }) : /*#__PURE__*/ _jsx("span", {
                                            className: "text-foreground",
                                            children: doc.title || 'Document'
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "shrink-0 text-[length:var(--fs-ui-meta)] tabular-nums text-muted-foreground",
                                        children: doc.docDate ? doc.docDate.toDate().toISOString().slice(0, 10) : '—'
                                    })
                                ]
                            }, doc.id);
                        })
                    })
                ]
            }),
            canEdit && /*#__PURE__*/ _jsxs("div", {
                className: "mt-3 space-y-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1.5",
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                htmlFor: categoryFieldId,
                                children: "Category"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: category,
                                onValueChange: setCategory,
                                disabled: busy,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        id: categoryFieldId,
                                        "data-testid": Testids.deliveryShared.drawerDocCategory,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsx(SelectContent, {
                                        children: DOCUMENT_CATEGORY_OPTIONS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                value: o.value,
                                                children: o.label
                                            }, o.value))
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(FileUploader, {
                        files: stagedFiles,
                        onChange: (f)=>setStagedFiles(f),
                        disabled: busy,
                        inputTestid: Testids.deliveryShared.drawerDocUploadInput
                    }),
                    stagedFiles.length > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        onClick: ()=>void handleUpload(),
                        disabled: busy,
                        "data-testid": Testids.deliveryShared.drawerDocUploadSubmit,
                        children: busy ? 'Uploading…' : 'Upload document'
                    })
                ]
            }),
            previewing && /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: previewing.fileId,
                title: previewing.title || 'Document',
                open: !!previewing,
                onOpenChange: (open)=>!open && setPreviewing(null)
            })
        ]
    });
}
